import { useCallback, useContext, useMemo, useRef, useState } from 'react';
import { v4 as uuid } from 'uuid';

import { BannerSeverity } from '@rover/kibble/official-patterns/Banner/BannerSeverity.constants';

import type { ShowToastOptions, ToastHandle, ToastLink, ToastRecord } from './Toast.common';
import ToastContext from './ToastContext';
import { addToast, markExiting, removeToast, requestDismiss } from './toastListReducer';
import type { ToastStackProps } from './ToastStack.common';

/** @deprecated Use `ShowToastOptions`. */
export type ToastMessageProps = ShowToastOptions;

export type ToastProps = {
  /** Adds a toast to the list and returns its id, for `dismissToast(id)`. */
  showToast: (options: ShowToastOptions) => string;
  /** With an id, dismisses that toast; without one, the newest. */
  dismissToast: (id?: string) => void;
  dismissAllToasts: () => void;
  /** Oldest first. */
  toasts: readonly ToastRecord[];
  /** For a caller hosting a stack of its own. Under a provider, the stack is already drawn. */
  toastStackProps: Required<Pick<ToastStackProps, 'toasts' | 'onDismiss' | 'onExitStart'>>;

  /** @deprecated Call `showToast` and let the provider draw it. Follows the newest toast. */
  isToastVisible: boolean;
  /** @deprecated Call `showToast` and let the provider draw it. Follows the newest toast. */
  toastProperties?: {
    ref: React.RefObject<ToastHandle | null>;
    onClose: () => void;
    children: React.ReactNode;
    severity?: `${BannerSeverity}`;
    title?: React.ReactNode;
    link?: ToastLink;
  };
  /** @deprecated Call `showToast` and let the provider draw it. The newest toast's id. */
  toastKey: string;
};

/**
 * A toast list of its own, for a caller that renders its own `<ToastStack>` rather than using the
 * app's. `ToastProvider` is the one that should: everywhere else, `useToast` is what to reach for.
 */
export const useToastList = (): ToastProps => {
  const [toasts, setToasts] = useState<readonly ToastRecord[]>([]);
  // Mirrors `toasts`, so two calls in one tick see each other and the id comes back at once.
  const toastsRef = useRef(toasts);
  const legacyToastRef = useRef<ToastHandle>(null);
  // Keeps `toastKey` stable while the list is empty, and distinct from any toast's id.
  const [emptyKey] = useState(uuid);

  const update = useCallback((next: readonly ToastRecord[]): void => {
    toastsRef.current = next;
    setToasts(next);
  }, []);

  const showToast = useCallback(
    (options: ShowToastOptions): string => {
      const { toasts: next, id } = addToast(toastsRef.current, options);
      update(next);
      return id;
    },
    [update]
  );

  const dismissToast = useCallback(
    (id?: string): void => {
      const { current } = toastsRef;
      // The legacy host holds the newest toast in this ref; the stack watches `isDismissing`.
      if (id === undefined) legacyToastRef.current?.dismiss();

      const target = id ?? current[current.length - 1]?.id;
      if (target !== undefined) update(requestDismiss(current, target));
    },
    [update]
  );

  const dismissAllToasts = useCallback((): void => {
    legacyToastRef.current?.dismiss();
    update(toastsRef.current.map((toast) => ({ ...toast, isDismissing: true })));
  }, [update]);

  const onDismiss = useCallback(
    (id: string): void => {
      update(removeToast(toastsRef.current, id));
    },
    [update]
  );

  // The front of the exit, where `onDismiss` is the back: the record outlives it by the exit.
  const onExitStart = useCallback(
    (id: string): void => {
      update(markExiting(toastsRef.current, id));
    },
    [update]
  );

  const toastStackProps = useMemo(
    () => ({ toasts, onDismiss, onExitStart }),
    [toasts, onDismiss, onExitStart]
  );

  // Stable across renders that change nothing, so a host can put it straight into a context.
  return useMemo((): ToastProps => {
    const newest = toasts[toasts.length - 1];

    return {
      showToast,
      dismissToast,
      dismissAllToasts,
      toasts,
      toastStackProps,

      isToastVisible: toasts.length > 0,
      toastKey: newest?.id ?? emptyKey,
      toastProperties: {
        ref: legacyToastRef,
        // One toast on screen, so its close clears the list rather than stranding older records.
        onClose: () => update([]),
        children: newest?.message ?? '',
        severity: newest?.severity ?? BannerSeverity.INFO,
        title: newest?.title,
        link: newest?.link,
      },
    };
  }, [toasts, showToast, dismissToast, dismissAllToasts, toastStackProps, emptyKey, update]);
};

/**
 * Shows toasts from anywhere under the app's `ToastProvider`, in the stack that provider renders.
 *
 * Without one it falls back to a list of its own, which the caller has to render a `<ToastStack>`
 * for — the pattern every screen used before the provider existed.
 */
const useToast = (): ToastProps => {
  const provided = useContext(ToastContext);
  const own = useToastList();

  return provided ?? own;
};

export default useToast;
